import React, { useState, useEffect, useContext } from 'react'

import { Button, Grid, Header, Image, Modal, Segment } from 'semantic-ui-react'

import UploadFromComputerImage from '../../_images/upload-form-computer.png'
import UploadFromPhoneImage from '../../_images/upload-from-phone.png'
import UploadFromPexelsImage from '../../_images/upload-from-sample-library.png'
import Data, { IClip, IProject, ProjectQuestionResponse } from '../../api/Data'
import { GlobalContext } from '../../contexts/GlobalContext'

import UploadForm from './UploadForm'
import UploadFromComputerModal, { AdminUploadDraft } from './UploadFromComputerModal'
import PexelsLibraryModal from './UploadFromPexelsModal'
import UploadFromPhoneModal from './UploadFromPhoneModal'

const UploadModal = ({ project, setShowModal }: IProps) => {
  // Global
  const { isMobile } = useContext(GlobalContext)

  // States
  const [step, setStep] = useState(1)
  const [clipIds] = useState<number[]>([])
  const [uploads, setUploads] = useState<IClip[]>([])
  const [adminUploadDraft, setAdminUploadDraft] = useState<AdminUploadDraft | null>(null)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [pendingContributorNames, setPendingContributorNames] = useState<string[]>([])
  const [pendingStockVideo, setPendingStockVideo] = useState<{
    videoId: number
    videoUrl: string
    contributorUrl: string
  } | null>(null)

  const [showFromComputerModal, setShowFromComputerModal] = useState(false)
  const [showFromPhoneModal, setShowFromPhoneModal] = useState(false)
  const [showFromPexelsModal, setShowFromPexelsModal] = useState(false)
  const [projectQuestions, setProjectQuestions] = useState<ProjectQuestionResponse[] | null>(null)

  // Scroll up after each step
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [step])

  useEffect(() => {
    fetchProjectQuestions(project.id)
  }, [project])

  useEffect(() => {
    if (!adminUploadDraft || showFromComputerModal) return
    setStep(2)
  }, [adminUploadDraft, showFromComputerModal])

  const { projectUrl, displaySetting } = project

  const fetchProjectQuestions = async (projectId: number) => {
    if (!projectId) return

    const { data, error } = await Data.getProjectQuestions(projectId, true)

    if (error) {
      console.error('Failed to fetch project questions:', error)
      return
    }
    setProjectQuestions(data)

  }


  return (
    <>
      {step === 1 && (
        <Modal dimmer="blurring" open>
          <Modal.Header>Admin upload tool</Modal.Header>
          <Modal.Content>
            <Header as="h3">
              Need to manually upload videos sent to you through another channel, test the mobile contributor
              experience, or add sample footage from our stock library?
              <Header.Subheader>As a project administrator, choose one of the three options below.</Header.Subheader>
            </Header>
            <Grid columns={3} textAlign="center" verticalAlign="middle" style={{ height: '300px' }}>
              <Grid.Column stretched>
                <Segment
                  onClick={() => {
                    setPendingFiles([])
                    setPendingContributorNames([])
                    setPendingStockVideo(null)
                    setShowFromComputerModal(true)
                  }}
                >
                  <Image src={UploadFromComputerImage} size="small" style={{ margin: '0 auto', cursor: 'pointer' }} />
                  <Header as="h3">Upload received videos</Header>
                </Segment>
              </Grid.Column>
              <Grid.Column stretched>
                <Segment onClick={() => setShowFromPhoneModal(true)}>
                  <Image src={UploadFromPhoneImage} size="small" style={{ margin: '0 auto', cursor: 'pointer' }} />
                  <Header as="h3">Test mobile upload</Header>
                </Segment>
              </Grid.Column>
              <Grid.Column stretched>
                <Segment onClick={() => setShowFromPexelsModal(true)}>
                  <Image
                    src={UploadFromPexelsImage}
                    alt="Pexels sample video"
                    size="small"
                    style={{ margin: '0 auto', cursor: 'pointer' }}
                  />
                  <Header as="h3">Add Pexels sample video</Header>
                </Segment>
              </Grid.Column>
            </Grid>
          </Modal.Content>
          <Modal.Actions>
            <Button fluid={isMobile} onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Actions>
        </Modal>
      )}

      {step === 2 && (
        <UploadForm
          clipIds={clipIds}
          displaySetting={displaySetting}
          projectQuestions={projectQuestions}
          uploads={uploads}
          setUploads={setUploads}
          setShowModal={setShowModal}
          adminUploadDraft={adminUploadDraft}
        />
      )}
      {/* From computer */}
      {showFromComputerModal && (
        <UploadFromComputerModal
          project={project}
          initialFiles={pendingFiles}
          initialContributorNames={pendingContributorNames}
          stockVideo={pendingStockVideo || undefined}
          onUploaded={(draft) => {
            setAdminUploadDraft(draft)
          }}
          setShowModal={setShowFromComputerModal}
        />
      )}

      {/* From phone */}
      {showFromPhoneModal && <UploadFromPhoneModal projectUrl={projectUrl} setShowModal={setShowFromPhoneModal} />}

      {/* From Pexels  */}
      {showFromPexelsModal && (
        <PexelsLibraryModal
          onVideoSelected={(file, attribution) => {
            setPendingFiles([file])
            setPendingContributorNames([attribution.contributorName])
            setPendingStockVideo({
              videoId: attribution.videoId,
              videoUrl: attribution.videoUrl,
              contributorUrl: attribution.contributorUrl,
            })
            setShowFromPexelsModal(false)
            setShowFromComputerModal(true)
          }}
          setShowModal={setShowFromPexelsModal}
        />
      )}
    </>
  )
}

interface IProps {
  project: IProject
  setShowModal: (arg: boolean) => void
}

export default UploadModal
